/**
 * ToolRenderers — per-tool render descriptors for the chat activity timeline
 * (docs/plans/2026-07-04_CHAT_UI_REDESIGN.md §2).
 *
 * The render language is a "verb · object · result-glance" ROW:
 *
 *   [icon] Verb Object            glance · badges · duration  ✓/✗
 *      └─ optional tool-specific DETAIL body behind the chevron
 *
 * Principles (why this replaces the old INPUT/OUTPUT boxes):
 *   • A finished tool's interesting datum is its RESULT — surface a glance
 *     of it in the row (line/match/file counts, exit code, diff badge),
 *     don't hide everything behind an expand.
 *   • Workspace paths render as FilePill — the SAME chip markdown file
 *     references use, so "a file" looks identical everywhere in chat.
 *   • Detail bodies are tool-specific (terminal, DiffView, result list) —
 *     the raw input/output dump survives only as the unknown-tool fallback.
 *   • Errors surface as a red line under the row, not inside the expand.
 */
import type { ReactNode, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useState } from 'react'
import {
  Terminal, FileText, Search, Pencil, FolderOpen, Globe,
  FileSearch, SquareFunction, BookOpen, History, FileType, LayoutTemplate, ListChecks, KeyRound,
  Layers, ImageOff, XCircle, ChevronRight, ChevronDown,
} from 'lucide-react'
import { BashOutput } from './BashOutput.js'
import { DiffView, DiffBadge } from './DiffView.js'
import { diffTextOf, type FileDiffEntry } from '../core/filediff.js'
import { FilePill } from './FilePill.js'
import { TodoList } from './TodoList.js'
import type { TodoItem } from './hooks.js'
import { A2UISurface } from './a2ui/A2UISurface.js'
import { parseA2UISurface } from './a2ui/types.js'
import type { A2UIEvent } from './a2ui/types.js'
// @papan-only: collapse terminal progress (ANSI + carriage-return) for
// non-bash tools — bash routes through BashOutput which already applies it.
import { collapseProgressOutput } from '@papan/types/text'

export interface ToolRenderProps {
  toolName: string
  input: Record<string, unknown>
  output?: string
  error?: string
  status: 'pending' | 'running' | 'completed' | 'error'
  title?: string
  metadata?: Record<string, unknown>
  wrapLines?: boolean
  onPathClick?: (path: string) => void
  /** Render inert: no control may act on the chat (e.g. a helper's step shown
   *  inside the parent's expanded helper view — its surface belongs to the
   *  helper's agent, not the parent's). Opening a file stays allowed. */
  readOnly?: boolean
}

export interface ToolRowSpec {
  /** Leading verb, e.g. "Read", "Ran", "Searched" (present tense while running) */
  verb: string
  /** The thing acted on — FilePill / mono pattern / plain text */
  object?: ReactNode
  /** Muted result summary shown after the object once completed */
  glance?: ReactNode
}

export interface ToolDescriptor {
  icon: typeof Terminal
  row: (p: ToolRenderProps) => ToolRowSpec
  /** Tool-specific expandable body; return null for "row only" */
  detail?: (p: ToolRenderProps) => ReactNode | null
  /** Start with the detail OPEN (rich output the user should see without
   *  a click — e.g. an A2UI surface). Collapsing stays available. */
  defaultExpanded?: (p: ToolRenderProps) => boolean
}

/* ─── shared row fragments ──────────────────────────────────────────── */

/** Monospace inline token (patterns, commands, queries) */
function Mono({ children }: { children: ReactNode }) {
  return <code className="font-mono text-[11px] px-1 py-px rounded bg-black/15 themed-text-secondary">{children}</code>
}

function countLines(s?: string): number {
  if (!s) return 0
  const t = s.replace(/\n$/, '')
  return t ? t.split('\n').length : 0
}

function fmtCount(n: number, noun: string): string {
  return `${n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n} ${noun}${n === 1 ? '' : 's'}`
}

function isPapanPath(s: string): boolean {
  return s.startsWith('/papan/') || /^\/?_system\//.test(s)
}

function pathObject(p: ToolRenderProps, path: unknown): ReactNode {
  const s = String(path || '')
  if (!s) return null
  return isPapanPath(s)
    ? <FilePill path={s} onClick={p.onPathClick} />
    : <Mono>{s}</Mono>
}

/** Scrollable mono text body — the shared "plain text detail" shape. */
function TextDetail({ text, wrapLines, error }: { text: string; wrapLines?: boolean; error?: boolean }) {
  return (
    <pre className={`p-2 rounded-md text-xs font-mono max-h-[240px] overflow-y-auto bg-black/15 ${
      error ? 'text-red-400' : 'themed-text-secondary'
    } ${wrapLines ? 'whitespace-pre-wrap break-words' : 'overflow-x-auto'}`}>
      {collapseProgressOutput(text)}
    </pre>
  )
}

/** Result-list detail: one row per output line, /papan/ lines as pills. */
function PathListDetail({ output, onPathClick }: { output: string; onPathClick?: (path: string) => void }) {
  const lines = output.replace(/\n$/, '').split('\n').slice(0, 50)
  return (
    <div className="flex flex-col items-start gap-0.5 py-0.5">
      {lines.map((line, i) => {
        const t = line.trim()
        return isPapanPath(t)
          ? <FilePill key={i} path={t} label={t.replace(/^\/papan\//, '')} onClick={onPathClick} />
          : <span key={i} className="text-xs font-mono themed-text-muted">{line}</span>
      })}
    </div>
  )
}

/** The diff entries a tool part carries. Either shape (#768): `diff` since the
 *  change, `before`/`after` on older rows — only `diffTextOf` tells them apart. */
function diffsOf(p: ToolRenderProps): FileDiffEntry[] {
  const m = p.metadata
  if (!m) return []
  if (m.filediff) return [m.filediff as any]
  if (Array.isArray(m.files)) return m.files as any[]
  return []
}

function DiffGlance({ p }: { p: ToolRenderProps }) {
  const diffs = diffsOf(p)
  const additions = diffs.reduce((s, d) => s + d.additions, 0)
  const deletions = diffs.reduce((s, d) => s + d.deletions, 0)
  return <DiffBadge additions={additions} deletions={deletions} />
}

function DiffDetail({ p }: { p: ToolRenderProps }) {
  const diffs = diffsOf(p)
  if (!diffs.length) return null
  return (
    <div className="space-y-2">
      {diffs.map((d, i) => <DiffView key={d.file + i} diff={diffTextOf(d)} file={d.file} />)}
    </div>
  )
}

function truncate(s: string, len: number): string {
  return s.length > len ? s.slice(0, len - 1) + '…' : s
}

function hostOf(url: unknown): string {
  try { return new URL(String(url)).host } catch { return String(url || '') }
}

/* ─── per-tool descriptors ──────────────────────────────────────────── */

/** The sandbox appends "[exit code: N]" to bash output for non-zero exits;
 *  the tool itself only errors on infra failure. Parse it so the row and
 *  the terminal badge reflect the COMMAND's outcome, not just the tool's. */
function bashExitCode(p: ToolRenderProps): number {
  if (p.error) return 1
  const m = (p.output || '').match(/\[exit code: (\d+)\]\s*$/)
  return m ? Number(m[1]) : 0
}

const bash: ToolDescriptor = {
  icon: Terminal,
  row: (p) => {
    const desc = typeof p.input.description === 'string' ? p.input.description : ''
    const cmd = String(p.input.command || '')
    const exit = p.status === 'completed' ? bashExitCode(p) : 0
    return {
      verb: p.status === 'running' || p.status === 'pending' ? 'Running' : 'Ran',
      object: desc
        ? <span className="inline-flex items-baseline gap-1.5 min-w-0">
            <span className="truncate">{desc}</span>
            {cmd && <Mono>{truncate(cmd, 48)}</Mono>}
          </span>
        : <Mono>{truncate(cmd, 64)}</Mono>,
      glance: p.status === 'completed' && p.output != null
        ? (exit !== 0
            ? <span className="text-red-400">exit {exit}</span>
            : fmtCount(countLines(p.output), 'line'))
        : undefined,
    }
  },
  detail: (p) => (p.output || p.error)
    ? <BashOutput output={p.output || p.error || ''} command={String(p.input.command || '')} exitCode={bashExitCode(p)} wrapLines={p.wrapLines} />
    : null,
}

const readFile: ToolDescriptor = {
  icon: FileText,
  row: (p) => ({
    verb: p.status === 'completed' ? 'Read' : 'Reading',
    object: pathObject(p, p.input.path),
    glance: p.output != null ? fmtCount(countLines(p.output), 'line') : undefined,
  }),
  // No detail: the pill opens the real file — richer than an echo of it.
}

const writeFile: ToolDescriptor = {
  icon: FileText,
  row: (p) => {
    const isNew = diffsOf(p).some(d => d.isNew)
    return {
      verb: p.status === 'completed' ? (isNew ? 'Created' : 'Wrote') : 'Writing',
      object: pathObject(p, p.input.path),
      glance: <DiffGlance p={p} />,
    }
  },
  detail: (p) => <DiffDetail p={p} />,
}

const editFile: ToolDescriptor = {
  icon: Pencil,
  row: (p) => ({
    verb: p.status === 'completed' ? 'Edited' : 'Editing',
    object: pathObject(p, p.input.path),
    glance: <DiffGlance p={p} />,
  }),
  detail: (p) => <DiffDetail p={p} />,
}

const applyPatch: ToolDescriptor = {
  icon: Pencil,
  row: (p) => {
    const diffs = diffsOf(p)
    return {
      verb: p.status === 'completed' ? 'Patched' : 'Patching',
      object: diffs.length === 1
        ? pathObject(p, diffs[0].file)
        : <span>{diffs.length ? fmtCount(diffs.length, 'file') : 'files'}</span>,
      glance: <DiffGlance p={p} />,
    }
  },
  detail: (p) => <DiffDetail p={p} />,
}

const glob: ToolDescriptor = {
  icon: FolderOpen,
  row: (p) => ({
    verb: p.status === 'completed' ? 'Matched' : 'Matching',
    object: <Mono>{String(p.input.pattern || '')}</Mono>,
    glance: p.output != null ? fmtCount(countLines(p.output), 'file') : undefined,
  }),
  detail: (p) => p.output ? <PathListDetail output={p.output} onPathClick={p.onPathClick} /> : null,
}

const grep: ToolDescriptor = {
  icon: Search,
  row: (p) => ({
    verb: p.status === 'completed' ? 'Searched' : 'Searching',
    object: (
      <span className="inline-flex items-baseline gap-1.5 min-w-0">
        <Mono>{String(p.input.pattern || '')}</Mono>
        {typeof p.input.path === 'string' && p.input.path && (
          <span className="themed-text-muted truncate">in {String(p.input.path).replace(/^\/papan\//, '')}</span>
        )}
      </span>
    ),
    // grep output shapes vary (counts, matches); first line is the best glance
    glance: p.output ? truncate(p.output.split('\n')[0], 48) : undefined,
  }),
  detail: (p) => p.output && countLines(p.output) > 1 ? <TextDetail text={p.output} wrapLines={p.wrapLines} /> : null,
}

const listFiles: ToolDescriptor = {
  icon: FolderOpen,
  row: (p) => ({
    verb: p.status === 'completed' ? 'Listed' : 'Listing',
    object: pathObject(p, p.input.path || '/'),
    glance: p.output != null ? fmtCount(countLines(p.output), 'entry').replace('entrys', 'entries') : undefined,
  }),
  detail: (p) => p.output ? <PathListDetail output={p.output} onPathClick={p.onPathClick} /> : null,
}

const webSearch: ToolDescriptor = {
  icon: Globe,
  row: (p) => ({
    verb: p.status === 'completed' ? 'Searched web' : 'Searching web',
    object: <span className="truncate">“{String(p.input.query || '')}”</span>,
  }),
  detail: (p) => p.output ? <TextDetail text={p.output} wrapLines={p.wrapLines} /> : null,
}

const webfetch: ToolDescriptor = {
  icon: Globe,
  row: (p) => ({
    verb: p.status === 'completed' ? 'Fetched' : 'Fetching',
    object: <Mono>{hostOf(p.input.url)}</Mono>,
    glance: p.output ? truncate(p.output.split('\n')[0], 48) : undefined,
  }),
  detail: (p) => p.output && countLines(p.output) > 1 ? <TextDetail text={p.output} wrapLines={p.wrapLines} /> : null,
}

const skill: ToolDescriptor = {
  icon: BookOpen,
  row: (p) => ({
    verb: p.status === 'completed' ? 'Loaded skill' : 'Loading skill',
    object: <Mono>{String(p.input.name || '')}</Mono>,
  }),
  detail: (p) => p.output ? <TextDetail text={p.output} wrapLines={p.wrapLines} /> : null,
}

const fileVersion: ToolDescriptor = {
  icon: History,
  row: (p) => ({
    verb: p.status === 'completed' ? 'Checked versions' : 'Checking versions',
    object: pathObject(p, p.input.path),
    glance: p.output != null ? fmtCount(countLines(p.output), 'version') : undefined,
  }),
  detail: (p) => p.output ? <TextDetail text={p.output} wrapLines={p.wrapLines} /> : null,
}

/** officecli verbs that only READ (no mutation). Mirror of
 *  OFFICECLI_READ_VERBS in features/agent/tools/edit-office-cli.ts — kept
 *  in sync by hand (that module is server-only, can't be imported here).
 *  Only drives the RUNNING label guess; the completed label uses the
 *  authoritative metadata.modifiedPaths signal. */
const OFFICE_READ_VERBS = new Set(['get', 'query', 'view', 'dump', 'validate', 'raw'])

/** Read-only? True/false when knowable from input, null when opaque
 *  (e.g. a LibreOffice script). office_tool → all-read command batch;
 *  libre_office_tool → explicit `readonly` flag. */
function officeIsReadOnly(input: Record<string, unknown>): boolean | null {
  if (input.readonly === true) return true
  if (Array.isArray(input.commands) && input.commands.length > 0) {
    return input.commands.every(c =>
      typeof (c as { command?: unknown })?.command === 'string'
      && OFFICE_READ_VERBS.has(((c as { command: string }).command).toLowerCase()))
  }
  return null
}

const officeTool: ToolDescriptor = {
  icon: FileType,
  row: (p) => {
    const running = p.status === 'running' || p.status === 'pending'
    // Authoritative outcome (completed): the write path sets
    // metadata.modifiedPaths on BOTH office tools; a read-only inspection
    // leaves it unset. So the label reflects what actually happened,
    // never claiming an edit for a read.
    const modified = Array.isArray(p.metadata?.modifiedPaths) ? (p.metadata!.modifiedPaths as unknown[]) : []
    const wrote = modified.length > 0
    const readHint = officeIsReadOnly(p.input) // running-state guess
    const verb = running
      ? (readHint === true ? 'Reading document' : readHint === false ? 'Editing document' : 'Working on document')
      : (wrote ? 'Edited document' : 'Read document')
    return {
      verb,
      object: pathObject(p, p.input.file || p.input.path) || <span className="truncate">{p.title || ''}</span>,
      glance: wrote && !running ? fmtCount(modified.length, 'file') : undefined,
    }
  },
  detail: (p) => p.output ? <TextDetail text={p.output} wrapLines={p.wrapLines} /> : null,
}

/** Display-mode A2UI surface delivered via tool metadata (`metadata.a2ui`).
 *  Buttons on a DISPLAY surface prefill the composer with the event (the
 *  user reviews + sends) — the blocking/interactive path is the question
 *  channel, not this tool. See a2ui/types.ts + design doc §4. */
function A2UIDisplayDetail({ p }: { p: ToolRenderProps }) {
  const surface = parseA2UISurface(p.metadata?.a2ui)
  if (!surface) return null
  // An ask-mode surface that was already answered/dismissed renders FROZEN
  // in scrollback — the server merged the user's final data model into the
  // persisted surface, so it shows what was chosen.
  const answered = !!p.metadata?.a2uiReply
  // A read-only row (a helper's step) is frozen like an answered surface: its
  // events would prefill the PARENT composer, whose agent never produced it.
  const frozen = answered || !!p.readOnly
  const onEvent = (ev: A2UIEvent) => {
    // Static action context + live data model, merged — the agent reads
    // both (e.g. {segment:'enterprise'} from the button + form state).
    const info = { ...(ev.context ?? {}), ...ev.data }
    const suffix = Object.keys(info).length > 0 ? ` ${JSON.stringify(info)}` : ''
    window.dispatchEvent(new CustomEvent('papan:chat-prefill', {
      detail: { text: `[ui:${ev.name}]${suffix}` },
    }))
  }
  // No wrapper chrome — payloads bring their own Card; double-boxing a
  // Card-rooted surface looked like a frame in a frame.
  return (
    <div className="max-w-md py-0.5">
      <A2UISurface surface={surface} onEvent={frozen ? undefined : onEvent} disabled={frozen} onOpenFile={p.onPathClick} />
    </div>
  )
}

/** Compact human summary of what the user chose in an ask-mode surface —
 *  the non-empty scalar leaves of the returned data model, deduped (the
 *  /_ui fallback + the model's own keys often carry the same value). */
function summarizeA2UIReply(reply: unknown): string | undefined {
  if (!reply || typeof reply !== 'object') return undefined
  const r = reply as { dismissed?: boolean; data?: unknown; raw?: unknown }
  if (r.dismissed) return 'dismissed'
  if (!r.data || typeof r.data !== 'object') return r.raw != null ? truncate(String(r.raw), 44) : undefined
  const vals: string[] = []
  const seen = new Set<string>()
  const walk = (v: unknown) => {
    if (v == null) return
    if (Array.isArray(v)) { v.forEach(walk); return }
    if (typeof v === 'object') { Object.values(v as object).forEach(walk); return }
    const s = String(v).trim()
    if (s && !seen.has(s)) { seen.add(s); vals.push(s) }
  }
  walk(r.data)
  return vals.length ? truncate(vals.join(' · '), 48) : undefined
}

// The row reads as a natural sentence, distinct by mode — NO "N components"
// (dev noise). display → "Showed <title>"; ask → "Asked <title>" + the
// user's choice as the glance. `title` is the model's human label.
const renderUi: ToolDescriptor = {
  icon: LayoutTemplate,
  row: (p) => {
    const running = p.status === 'running' || p.status === 'pending'
    const reply = p.metadata?.a2uiReply
    const isAsk = p.input.mode === 'ask' || !!reply
    const title = typeof p.input.title === 'string' && p.input.title.trim()
      ? p.input.title.trim()
      : (isAsk ? 'your input' : 'a view')
    return {
      verb: isAsk
        ? (running ? 'Requesting' : 'Asked')
        : (running ? 'Preparing' : 'Showed'),
      object: <span className="truncate">{title}</span>,
      glance: !running && isAsk ? summarizeA2UIReply(reply) : undefined,
    }
  },
  detail: (p) => (p.metadata?.a2ui ? <A2UIDisplayDetail p={p} /> : null),
  defaultExpanded: (p) => !!parseA2UISurface(p.metadata?.a2ui),
}

/* ─── credential tools — the keybook "key" language ─────────────────────
 * cred_to_env = asking the user for a key; cred_used = reporting whether
 * the key worked. Rows speak in keys (gold, like the keybook fobs), and
 * env var NAMES (never values — those are server-side only) render as
 * amber chips instead of the raw JSON/how-to dump. */

/** Unique $ENV_VAR names mentioned in a cred_to_env output, in order. */
export function credEnvVars(output?: string): string[] {
  if (!output) return []
  const seen = new Set<string>()
  // a line saying a variable is NOT set names it without offering it (#1448)
  for (const line of output.split('\n')) {
    if (line.includes('NOT set')) continue
    for (const m of line.matchAll(/\$([A-Z][A-Z0-9_]{2,})/g)) seen.add(m[1])
  }
  // Hidden-provider internals (e.g. __PAPAN_CRED_GITHUB_x) are plumbing,
  // not something the user should read.
  return [...seen].filter(v => !v.startsWith('__'))
}

function EnvChip({ name }: { name: string }) {
  return (
    <code className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300/90 border border-amber-500/20">
      ${name}
    </code>
  )
}

const credToEnv: ToolDescriptor = {
  icon: KeyRound,
  row: (p) => {
    const running = p.status === 'running' || p.status === 'pending'
    const label = typeof p.input.label === 'string' && p.input.label ? p.input.label : ''
    const object = label
      ? <span className="truncate">{label}</span>
      : (pathObject(p, p.input.file) ?? <span className="truncate">credentials</span>)
    if (running) return { verb: 'Requesting key', object }
    const out = p.output ?? ''
    const deferred = out.includes('provide credentials later')
    const ready = out.includes('Credentials are ready') || out.includes('auth configured')
    const vars = credEnvVars(out)
    return {
      verb: deferred ? 'Key deferred' : ready ? 'Key received' : 'Key request',
      object,
      glance: deferred
        ? 'user will provide later'
        : vars.length
          ? <span className="inline-flex items-center gap-1">{vars.slice(0, 2).map(v => <EnvChip key={v} name={v} />)}{vars.length > 2 && <span>+{vars.length - 2}</span>}</span>
          : undefined,
    }
  },
  detail: (p) => {
    const vars = credEnvVars(p.output)
    if (!vars.length) return p.output ? <TextDetail text={p.output} wrapLines={p.wrapLines} /> : null
    // Env chips instead of the model-facing how-to blob — names only.
    return (
      <div className="flex flex-wrap items-center gap-1.5 py-0.5">
        <span className="text-[10px] themed-text-muted">available as</span>
        {vars.map(v => <EnvChip key={v} name={v} />)}
      </div>
    )
  },
}

const credUsed: ToolDescriptor = {
  icon: KeyRound,
  row: (p) => {
    const running = p.status === 'running' || p.status === 'pending'
    const failed = p.input.status === 'failure'
    const envVar = typeof p.input.envVar === 'string' && p.input.envVar ? p.input.envVar : ''
    const file = typeof p.input.filePath === 'string' ? p.input.filePath : ''
    return {
      verb: running ? 'Checking key' : failed ? 'Key failed' : 'Key worked',
      object: envVar
        ? <EnvChip name={envVar.replace(/^\$/, '')} />
        : (pathObject(p, file) ?? <span className="truncate">credential</span>),
      glance: !running && failed && typeof p.input.reason === 'string'
        ? <span className="text-red-400">{truncate(p.input.reason, 44)}</span>
        : !running && file ? truncate(file.split('/').pop() ?? '', 32) : undefined,
    }
  },
  // Failures carry a recovery how-to worth reading; successes need nothing.
  detail: (p) => (p.input.status === 'failure' && p.output ? <TextDetail text={p.output} wrapLines={p.wrapLines} /> : null),
}

/** todowrite carries the full list in metadata.todos — surface the CURRENT
 *  focus (the in-progress item) in the row + a done/total glance, and the
 *  whole checklist in the detail (shared TodoList). Replaces the raw-JSON
 *  generic dump. */
function readTodos(p: ToolRenderProps): TodoItem[] {
  return Array.isArray(p.metadata?.todos) ? p.metadata!.todos as TodoItem[] : []
}

const todowrite: ToolDescriptor = {
  icon: ListChecks,
  row: (p) => {
    const todos = readTodos(p)
    const total = todos.length
    const done = todos.filter(t => t.status === 'completed' || t.status === 'cancelled').length
    const active = todos.find(t => t.status === 'in_progress')
    return {
      verb: 'Updated to-dos',
      object: active
        ? <span className="truncate">{active.content}</span>
        : <span className="themed-text-muted">{total ? fmtCount(total, 'to-do') : 'no to-dos'}</span>,
      glance: total > 0 ? `${done}/${total} done` : undefined,
    }
  },
  detail: (p) => {
    const todos = readTodos(p)
    return todos.length ? <TodoList todos={todos} animateInProgress={false} /> : null
  },
  // Small lists open by default — the checklist IS the point; large ones
  // stay collapsed behind the row summary.
  defaultExpanded: (p) => { const n = readTodos(p).length; return n > 0 && n <= 7 },
}

const task: ToolDescriptor = {
  icon: SquareFunction,
  row: (p) => ({
    verb: p.status === 'completed' ? 'Delegated' : 'Delegating',
    object: <span className="truncate">{String(p.input.description || p.input.prompt || 'task')}</span>,
  }),
  // detail handled specially in ToolCallBlock (child-session expand)
}

/* ─── batch ─────────────────────────────────────────────────────────── */

/** One sub-call's record, as `batch` writes it into tool metadata. */
interface BatchCall {
  i: number
  tool: string
  ok: boolean
  error?: string
  label?: string
  attachments?: number[]
  dropped?: { count: number; reason: 'count' | 'size' }
  /** #460 — per-sub-call renderable metadata (diff card `filediff`/`files`, bash
   *  `liveOutput` tail), folded in by `batch` so the sub-row renders THROUGH the
   *  sub-tool's descriptor instead of showing a bare icon + verb. */
  meta?: Record<string, unknown>
  /** #461 — this call's own output was trimmed to share the batch's output
   *  budget, so the model saw an INCOMPLETE result for it. */
  outputTrimmed?: boolean
}

function batchCalls(p: ToolRenderProps): BatchCall[] {
  const raw = p.metadata?.calls
  return Array.isArray(raw) ? (raw as BatchCall[]) : []
}

/** The arguments the model passed, straight off the tool part. Available from
 *  the moment the call starts — which is what lets the sub-rows render WHILE
 *  the batch is still running, before any metadata exists. */
function batchInputCalls(p: ToolRenderProps): Array<{ tool: string; parameters: Record<string, unknown> }> {
  const raw = (p.input as { tool_calls?: unknown }).tool_calls
  if (!Array.isArray(raw)) return []
  return raw.map(c => {
    const o = (c ?? {}) as { tool?: unknown; parameters?: unknown }
    return {
      tool: typeof o.tool === 'string' ? o.tool : 'tool',
      parameters: (o.parameters ?? {}) as Record<string, unknown>,
    }
  })
}

/** The diff cards a sub-call carries, straight off its folded `meta`. Mirrors
 *  `diffsOf`, but reads the sub-call record (`BatchSubRow` passes
 *  `metadata: call.meta` to the descriptor, but the DETAIL body reads the diffs
 *  itself so it can render them without a second descriptor round-trip). */
function subCallDiffs(meta: Record<string, unknown> | undefined): FileDiffEntry[] {
  if (!meta) return []
  if (meta.filediff) return [meta.filediff as any]
  // `.filter(Boolean)` defends the render against a sparse `files` array (a
  // future tool emitting a hole would otherwise throw inside DiffView on the
  // undefined entry). multiedit/apply_patch never produce one today.
  if (Array.isArray(meta.files)) return (meta.files as any[]).filter(Boolean)
  return []
}

/** Render one sub-call THROUGH ITS OWN TOOL'S DESCRIPTOR, so a batched
 *  `read_file` reads exactly like a standalone one — same icon, same verb,
 *  same FilePill — instead of a JSON blob. `batch` cannot nest (the tool
 *  refuses it), and the guard below keeps that true for the renderer even if
 *  the rule ever changes.
 *
 *  #460 — the sub-call's folded `meta` (diff card / bash tail) is passed to the
 *  descriptor's `row()` so a batched edit gets its own "+n −m" diff badge, and
 *  is expanded on click into the full DiffView (diffs are complete) or bash's
 *  output TAIL (`liveOutput`, the last ~8000 chars — full per-row streaming is
 *  the deferred per-persisted-part change, #460 option C). This rich detail used
 *  to render for ONE sub-call at most: they all wrote to the parent's metadata
 *  bucket, last-writer-wins. */
function BatchSubRow({ p, tool, parameters, call }: {
  p: ToolRenderProps
  tool: string
  parameters: Record<string, unknown>
  call?: BatchCall
}) {
  const descriptor = tool === 'batch' ? genericDescriptor : (TOOL_DESCRIPTORS[tool] ?? genericDescriptor)
  const failed = call ? !call.ok : false
  // The sub-call gets the PARENT's live status until the batch reports on it:
  // a finished batch cannot contain a still-running call.
  const status: ToolRenderProps['status'] = call ? (call.ok ? 'completed' : 'error') : p.status
  const spec = descriptor.row({
    toolName: tool,
    input: parameters,
    status,
    title: call?.label,
    // #460 — the per-sub-call metadata, so DiffGlance (edit/write/multiedit)
    // renders the badge in the glance. undefined for calls with no folded meta.
    metadata: call?.meta,
    onPathClick: p.onPathClick,
    wrapLines: p.wrapLines,
  })
  const Icon = descriptor.icon
  const dropped = call?.dropped

  const diffs = subCallDiffs(call?.meta)
  const liveOutput = typeof call?.meta?.liveOutput === 'string' ? (call.meta.liveOutput as string) : ''
  const command = typeof parameters.command === 'string' ? (parameters.command as string) : undefined
  const hasDetail = diffs.length > 0 || !!liveOutput.trim()
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="flex flex-col gap-0.5">
      <div
        className={`flex items-baseline gap-2 min-w-0 ${hasDetail ? 'cursor-pointer group/sub' : ''}`}
        {...(hasDetail ? { role: 'button', tabIndex: 0, onClick: () => setExpanded(v => !v), onKeyDown: (e: ReactKeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v) } } } : {})}
      >
        {failed
          ? <XCircle size={11} className="text-red-400 shrink-0 translate-y-px" />
          : <Icon size={11} className="themed-text-muted shrink-0 translate-y-px" />}
        <span className="min-w-0 flex items-baseline gap-1.5 text-xs">
          <span className="shrink-0 themed-text-secondary">{spec.verb}</span>
          {spec.object && <span className="min-w-0 truncate themed-text">{spec.object}</span>}
        </span>
        {spec.glance != null && (
          <span className="text-[11px] themed-text-muted shrink-0 truncate max-w-[40%]">{spec.glance}</span>
        )}
        {hasDetail && (
          expanded
            ? <ChevronDown size={10} className="themed-text-muted shrink-0 translate-y-px" />
            : <ChevronRight size={10} className="themed-text-muted shrink-0 opacity-0 group-hover/sub:opacity-100 transition-opacity translate-y-px" />
        )}
      </div>
      {/* 11px icon + 8px gap = the 19px indent the parent row uses. */}
      {expanded && diffs.length > 0 && (
        <div className="pl-[19px] space-y-2">
          {diffs.map((d, i) => <DiffView key={d.file + i} diff={diffTextOf(d)} file={d.file} />)}
        </div>
      )}
      {expanded && !diffs.length && liveOutput.trim() && (
        <div className="pl-[19px]">
          <BashOutput output={liveOutput} command={command} wrapLines={p.wrapLines} />
        </div>
      )}
      {call?.error && (
        <div className="pl-[19px] text-[11px] text-red-400 break-words">{call.error}</div>
      )}
      {call?.outputTrimmed && (
        <div className="pl-[19px] text-[11px] text-amber-400/90 break-words">
          Output trimmed to share the batch budget — the AI saw an incomplete result. Ask for this one on its own for the full output.
        </div>
      )}
      {dropped && (
        <div className="pl-[19px] flex items-baseline gap-1.5 text-[11px] text-amber-400/90">
          <ImageOff size={10} className="shrink-0 translate-y-px" />
          <span className="break-words">
            {dropped.count === 1 ? 'Image not sent to the AI' : `${dropped.count} images not sent to the AI`}
            {' — '}
            {dropped.reason === 'count'
              ? 'this batch already had the most images it can carry'
              : 'the images in this batch were too large together'}
            . Ask again for this file on its own.
          </span>
        </div>
      )}
    </div>
  )
}

const batch: ToolDescriptor = {
  icon: Layers,
  row: (p) => {
    const calls = batchCalls(p)
    const planned = calls.length || batchInputCalls(p).length
    const failed = calls.filter(c => !c.ok).length
    const droppedCount = calls.reduce((n, c) => n + (c.dropped?.count ?? 0), 0)
    const trimmedCount = calls.filter(c => c.outputTrimmed).length
    const parts: string[] = []
    if (failed) parts.push(`${failed} failed`)
    if (droppedCount) parts.push(droppedCount === 1 ? '1 image not sent' : `${droppedCount} images not sent`)
    if (trimmedCount) parts.push(trimmedCount === 1 ? '1 output trimmed' : `${trimmedCount} outputs trimmed`)
    return {
      verb: p.status === 'completed' || p.status === 'error' ? 'Ran' : 'Running',
      object: <span className="truncate">{planned === 1 ? '1 step' : `${planned} steps`} at once</span>,
      glance: parts.length ? parts.join(' · ') : undefined,
    }
  },
  detail: (p) => {
    const inputCalls = batchInputCalls(p)
    const calls = batchCalls(p)
    // Prefer the model's own argument list: it is present while running, and it
    // is what `calls[i].i` indexes into. Fall back to the records alone for a
    // resumed session whose input failed to persist.
    const rows = inputCalls.length
      ? inputCalls.map((c, i) => ({ tool: c.tool, parameters: c.parameters, call: calls.find(r => r.i === i) }))
      : calls.map(c => ({ tool: c.tool, parameters: {} as Record<string, unknown>, call: c }))
    if (!rows.length) return null
    return (
      <div className="space-y-1">
        {rows.map((r, i) => (
          <BatchSubRow key={i} p={p} tool={r.tool} parameters={r.parameters} call={r.call} />
        ))}
      </div>
    )
  },
  // The point of a batch row is WHAT RAN. One or two steps fit without pushing
  // the reply down the page; a long fan-out stays behind the chevron.
  defaultExpanded: (p) => {
    const n = batchCalls(p).length || batchInputCalls(p).length
    return n > 0 && n <= 4
  },
}

/** Error text body — used by ToolRow when a failed tool's descriptor has
 *  no error-bearing detail of its own (read_file, glob, …). */
export function TextDetailForError({ error, wrapLines }: { error: string; wrapLines?: boolean }) {
  return <TextDetail text={error} wrapLines={wrapLines} error />
}

/** Unknown / custom tools — the only place the raw dump survives. */
export const genericDescriptor: ToolDescriptor = {
  icon: FileSearch,
  row: (p) => ({
    verb: p.toolName,
    object: p.title ? <span className="truncate">{p.title}</span> : undefined,
  }),
  detail: (p) => {
    const input = JSON.stringify(p.input, null, 2)
    return (
      <div className="space-y-1.5">
        {input !== '{}' && <TextDetail text={input} wrapLines={p.wrapLines} />}
        {(p.output || p.error) && <TextDetail text={p.output || p.error || ''} wrapLines={p.wrapLines} error={!!p.error && !p.output} />}
      </div>
    )
  },
}

export const TOOL_DESCRIPTORS: Record<string, ToolDescriptor> = {
  bash,
  batch,
  render_ui: renderUi,
  todowrite,
  cred_to_env: credToEnv,
  cred_used: credUsed,
  read_file: readFile,
  write_file: writeFile,
  edit_file: editFile,
  multiedit: editFile,
  apply_patch: applyPatch,
  glob,
  grep,
  list_files: listFiles,
  web_search: webSearch,
  webfetch,
  task,
  skill,
  file_version: fileVersion,
  office_tool: officeTool,
  libre_office_tool: officeTool,
}
